import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Cookies from 'js-cookie';
import Topbar from '../layouts/Topbar';
import Sidebar from '../layouts/Sidebar';
import { FaEye, FaFilePdf, FaDownload } from 'react-icons/fa';
import { MdFlag } from 'react-icons/md';
import API_BASE_URL from '../../../config/api';

// Add a styled modal component
const Modal = ({ isOpen, onClose, children, style }) => {
  if (!isOpen) return null;
  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh',
      background: 'rgba(30, 41, 59, 0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
    }}>
      <div style={{
        background: '#fff',
        padding: '32px 28px 24px 28px',
        borderRadius: 16,
        minWidth: 340,
        maxWidth: 420,
        boxShadow: '0 8px 32px rgba(30,41,59,0.18)',
        position: 'relative',
        fontFamily: 'inherit',
        animation: 'modalFadeIn 0.2s',
        ...style,
      }}>
        {/* Close icon */}
        <button
          onClick={onClose}
          style={{
            position: 'absolute',
            top: 14,
            right: 14,
            background: 'none',
            border: 'none',
            fontSize: 22,
            color: '#64748b',
            cursor: 'pointer',
            fontWeight: 600,
            lineHeight: 1,
          }}
          aria-label="Close"
        >
          &times;
        </button>
        {children}
      </div>
      <style>{`
        @keyframes modalFadeIn {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};

const GoalReviews = () => {
  const [goals, setGoals] = useState([]);
  const [filteredGoals, setFilteredGoals] = useState([]);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [availableYears, setAvailableYears] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const baseUrl = API_BASE_URL;

  // View modal state
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [viewGoal, setViewGoal] = useState(null);

  useEffect(() => {
    const fetchKras = async () => {
      setLoading(true);
      setError(null);
      try {
        const token = Cookies.get('token');
        const response = await axios.get(`${baseUrl}/api/key-result-areas/`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });
        // Only include goals with status 'complete' or 'completed'
        const allGoals = (Array.isArray(response.data) ? response.data : [])
          .flatMap(kra => Array.isArray(kra.goals) ? kra.goals : [])
          .filter(goal => goal.status === 'complete' || goal.status === 'completed');
        setGoals(allGoals);
        // years
        const years = Array.from(new Set(allGoals.map(g => g?.year).filter(y => y !== undefined && y !== null))).sort((a,b)=>a-b);
        setAvailableYears(years);
        const effectiveYear = years.length && years.includes(selectedYear) ? selectedYear : (years[years.length-1] || selectedYear);
        setSelectedYear(effectiveYear);
        setFilteredGoals(allGoals.filter(g => (g.year !== undefined && g.year !== null) ? g.year === effectiveYear : true));
      } catch (err) {
        setError('Failed to fetch goals.');
      } finally {
        setLoading(false);
      }
    };
    fetchKras();
  }, []);

  // Update filtered goals when year changes
  useEffect(() => {
    if (goals.length > 0) {
      setFilteredGoals(goals.filter(g => (g.year !== undefined && g.year !== null) ? g.year === selectedYear : true));
    }
  }, [selectedYear, goals]);

  // Handle View button click
  const handleViewClick = (goal) => {
    setViewGoal(goal);
    setViewModalOpen(true);
  };

  // Generate report data (group by KRA)
  const generateReportData = () => {
    const kraMap = {};
    filteredGoals.forEach(g => {
      const kraName = (g.key_result_area && (g.key_result_area.name || g.key_result_area)) || g.kra_name || 'Uncategorized';
      if (!kraMap[kraName]) kraMap[kraName] = [];
      kraMap[kraName].push(g);
    });
    const totalHod = filteredGoals.reduce((s,g)=> s + (Number(g.hod_score)||0),0);
    const totalMetric = filteredGoals.reduce((s,g)=> s + (Number(g.metric)||0),0);
    const percentage = totalMetric > 0 ? ((totalHod/totalMetric)*100).toFixed(2) : '0.00';
    return {
      year: selectedYear,
      kras: Object.entries(kraMap),
      totalHod,
      totalMetric,
      percentage,
      goals: filteredGoals,
    };
  };

  // Generate Professional PDF Report (client-side)
  const generatePDF = async () => {
    try {
      const report = generateReportData();
      const { jsPDF } = await import('jspdf');
      const doc = new jsPDF('p','mm','a4');

      const primary = [59,130,246];
      const dark = [55,65,81];
      const light = [156,163,175];

      const wrap = (text, maxWidth) => {
        const words = String(text||'').split(' ');
        const lines=[]; let line='';
        words.forEach(w=>{ const t=line?(line+' '+w):w; if(doc.getTextWidth(t)>maxWidth && line){ lines.push(line); line=w; } else { line=t; } });
        if(line) lines.push(line); return lines;
      };

      const drawTable = (headers, rows, startY, maxWidth=180) => {
        const colWidths = [70,40,30,30,10]; // Activity, Standard, Metric, HOD, idx
        const headerH=10; const rowH=8; let x=15;
        doc.setFillColor(...primary); doc.rect(15,startY,maxWidth,headerH,'F');
        headers.forEach((h,i)=>{ doc.setTextColor(255,255,255); doc.setFontSize(10); doc.setFont(undefined,'bold'); doc.text(h, x+2, startY+6); x+=colWidths[i]; });
        let y = startY+headerH; doc.setTextColor(0,0,0); doc.setFont(undefined,'normal'); doc.setFontSize(9);
        rows.forEach((row, rIdx)=>{
          if(rIdx%2===0){ doc.setFillColor(249,250,251); doc.rect(15,y,maxWidth,rowH,'F'); }
          let xx=15; let maxLines=1;
          row.forEach((cell,i)=>{ const wl = wrap(cell, colWidths[i]-4); maxLines=Math.max(maxLines, wl.length); wl.forEach((ln,li)=>{ doc.text(ln, xx+2, y+5+(li*3.5)); }); xx+=colWidths[i]; });
          y += Math.max(rowH, maxLines*3.5+2);
        });
        return y;
      };

      let y=20;
      // Header
      doc.setTextColor(...primary); doc.setFontSize(18); doc.setFont(undefined,'bold');
      doc.text('KPI Reviews Report', 105, y, {align:'center'});
      y+=12; doc.setTextColor(...dark); doc.setFontSize(14); doc.setFont(undefined,'normal');
      doc.text(`Year: ${report.year}`, 105, y, {align:'center'});
      y+=16; doc.setFillColor(248,250,252); doc.rect(15,y,180,25,'F'); doc.setDrawColor(...primary); doc.rect(15,y,180,25);
      y+=8; doc.setTextColor(...dark); doc.setFontSize(13); doc.setFont(undefined,'bold'); doc.text('Summary', 20, y);
      y+=7; doc.setFontSize(11); doc.setFont(undefined,'normal'); doc.text(`Total HOD Score: ${report.totalHod} / ${report.totalMetric}`, 20, y);
      y+=5; doc.text(`Performance: ${report.percentage}%`, 20, y);
      y+=15;

      // KRA sections
      report.kras.forEach(([kraName, kraGoals])=>{
        if(y>250){ doc.addPage(); y=20; }
        doc.setFillColor(...primary); doc.rect(15,y,180,12,'F'); doc.setTextColor(255,255,255); doc.setFontSize(12); doc.setFont(undefined,'bold');
        doc.text(`KRA: ${kraName}`, 20, y+8); y+=15;
        const headers = ['Activity/KPI','Standard','Metric','HOD',''];
        const rows = kraGoals.map((g,i)=>[
          g.activity || 'N/A',
          g.performance_standard || 'N/A',
          (g.metric !== undefined ? g.metric : '-'),
          (g.hod_score !== undefined ? g.hod_score : '-'),
          ''
        ]);
        const kraTotal = kraGoals.reduce((s,g)=> s+(Number(g.hod_score)||0),0);
        const kraMetric = kraGoals.reduce((s,g)=> s+(Number(g.metric)||0),0);
        rows.push([`KRA Total`, '', `${kraMetric}`, `${kraTotal}`, '']);
        y = drawTable(headers, rows, y);
        y+=10;
      });

      // Footer
      const pages = doc.internal.getNumberOfPages();
      for(let i=1;i<=pages;i++){
        doc.setPage(i); doc.setTextColor(...light); doc.setFontSize(8);
        doc.text('Generated by PMS', 15, 290);
        doc.text(`Page ${i} of ${pages}`, 195, 290, {align:'right'});
      }
      const filename = `KPI Reviews Report ${selectedYear}.pdf`;
      doc.save(filename);
    } catch (e) {
      alert('Failed to generate PDF');
    }
  };

  return (
    <>
      <Topbar />
      <Sidebar />
      <div className="employee-goals-container">
        <div className="page-header">
          <div className="header-content" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 18 }}>
              <div className="page-title">
                <h1 style={{ margin: 0 }}>KPI Reviews</h1>
                <p style={{ margin: 0 }}>View your completed KPIs and HOD scores</p>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <label style={{ fontSize: 14, color: '#fff' }}>Year: </label>
                <select
                  value={selectedYear}
                  onChange={(e)=>setSelectedYear(parseInt(e.target.value))}
                  style={{ padding: '6px 10px', border: '1px solid #cbd5e1', borderRadius: 6, background: '#fff', color: '#0f172a', fontSize: 14, cursor: 'pointer' }}
                >
                  {(availableYears.length ? availableYears : [selectedYear]).sort((a,b)=>a-b).map(y=> (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
              </div>
              {filteredGoals.length > 0 && (
                <button onClick={generatePDF} title="Download PDF Report" style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#dc2626', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 12px', cursor: 'pointer' }}>
                  <FaFilePdf style={{ fontSize: 16 }} />
                  <FaDownload style={{ fontSize: 14 }} />
                </button>
              )}
            </div>
          </div>
        </div>
        <div className="hr-content-wrapper">
        {loading ? (
            <div className="loading-state">Loading KPIs...</div>
        ) : error ? (
            <div className="error-state">{error}</div>
          ) : (
            <>
            <div className="goals-table-wrapper">
              <table className="goals-table">
                <thead>
                  <tr>
                    <th>Activity / Key Performance Indicator</th>
                    <th>Performance Standard / Target</th>
                    <th>Metric</th>
                    <th>Self Score</th>
                    <th>HOD Score</th>
                    <th>Agreed Score</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredGoals.length === 0 ? (
                    <tr><td colSpan={6} className="empty-row">No completed KPIs found.</td></tr>
                  ) : (
                    (() => {
                      const kraMap = {};
                      filteredGoals.forEach(g => {
                        const kraName = (g.key_result_area && (g.key_result_area.name || g.key_result_area)) || g.kra_name || 'Uncategorized';
                        if (!kraMap[kraName]) kraMap[kraName] = [];
                        kraMap[kraName].push(g);
                      });
                      const grouped = Object.entries(kraMap);
                      return grouped.map(([kra, kraGoals], gidx) => (
                        <React.Fragment key={kra || gidx}>
                          <tr style={{ background: '#fffb' }}>
                            <th colSpan={6} style={{ padding: '10px 8px', textAlign: 'left', fontWeight: 600, color: '#334155', width: '100%' }}>
                              KRA: {kra}
                            </th>
                          </tr>
                          {kraGoals.map((goal, idx) => (
                            <tr key={goal.id || idx}>
                              <td>{goal.activity || '[No Activity]'}</td>
                              <td>{goal.performance_standard !== undefined ? goal.performance_standard : '-'}</td>
                              <td>{goal.metric !== undefined ? goal.metric : '-'}</td>
                              <td>{goal.score !== undefined ? goal.score : '-'}</td>
                              <td>{goal.hod_score !== undefined ? goal.hod_score : '-'}</td>
                              <td>{goal.agreed_score !== undefined ? goal.agreed_score : '-'}</td>

                              
                    </tr>
                  ))}
                          {/* KRA subtotal row */}
                          <tr>
                            <td colSpan={4} style={{ textAlign: 'right', fontWeight: 700, background: '#f8fafc' }}>KRA Final</td>
                            <td style={{ fontWeight: 800, color: '#0f172a', background: '#f8fafc' }}>
                              {kraGoals.reduce((s, g) => s + (Number(g.hod_score) || 0), 0)} / {kraGoals.reduce((s, g) => s + (Number(g.metric) || 0), 0)}
                            </td>
                            <td style={{ background: '#f8fafc' }}></td>
                          </tr>
                        </React.Fragment>
                      ))
                    })()
                  )}
                </tbody>
                
              </table>
            </div>
            {goals.length > 0 ? (() => {
              const totalHod = goals.reduce((sum, g) => sum + (Number(g.hod_score) || 0), 0);
              const totalMetric = goals.reduce((sum, g) => sum + (Number(g.metric) || 0), 0);
              const pct = totalMetric > 0 ? ((totalHod / totalMetric) * 100).toFixed(2) : '0.00';
              return (
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 20 }}>
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: 14,
                    background: '#ffffff',
                    border: '1px solid #e2e8f0',
                    borderRadius: 12,
                    padding: '10px 16px',
                    boxShadow: '0 2px 8px rgba(30,41,59,0.06)'
                  }}>
                    <div style={{ fontWeight: 700, color: '#0f172a' }}>Final Total</div>
                    <div style={{ fontWeight: 800, color: '#0f172a' }}>{totalHod} / {totalMetric}</div>
                    <div style={{
                      marginLeft: 8,
                      background: '#e0f2fe',
                      color: '#0369a1',
                      fontWeight: 800,
                      padding: '4px 10px',
                      borderRadius: 9999,
                      border: '1px solid #bae6fd'
                    }}>{pct}%</div>
                  </div>
                </div>
              );
            })() : null}
            
            </>
        )}
        </div>        
      </div>
      {/* Modal for viewing goal details */}
      <Modal style={{ minWidth: 640 }} isOpen={viewModalOpen} onClose={() => setViewModalOpen(false)}>
        <h2 style={{ margin: 0, marginBottom: 18, fontWeight: 700, fontSize: 22, color: '#0f172a', letterSpacing: 0.2 }}>Goal Details</h2>
        {viewGoal && (
          <>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: 0, minWidth: 640, background: '#f8fafc', borderRadius: 12, boxShadow: '0 1px 4px rgba(30,41,59,0.06)', overflow: 'hidden', border: '1px solid #e2e8f0' }}>
              {[
                { label: 'Activity / KPI', value: viewGoal.activity },
                { label: 'Performance Standard / Target', value: viewGoal.performance_standard },
                { label: 'Metric', value: viewGoal.metric || viewGoal.score },
                { label: 'Self Score', value: viewGoal.score },
                { label: 'HOD Score', value: viewGoal.hod_score !== undefined ? viewGoal.hod_score : '-' },
              ].map((row, idx, arr) => (
                <div
                  key={row.label}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '14px 20px',
                    background: idx % 2 === 0 ? '#fff' : '#f1f5f9',
                    borderBottom: idx !== arr.length - 1 ? '1px solid #e2e8f0' : 'none',
                    fontSize: 16,
                    transition: 'background 0.18s',
                  }}
                  onMouseOver={e => (e.currentTarget.style.background = '#e0e7ef')}
                  onMouseOut={e => (e.currentTarget.style.background = idx % 2 === 0 ? '#fff' : '#f1f5f9')}
                >
                  <span style={{ color: '#334155', fontWeight: 500 }}>{row.label}</span>
                  <span style={{ color: '#0f172a', fontWeight: 600, whiteSpace: 'pre-wrap' }}>{row.value}</span>
                </div>
              ))}
            </div>
          </>
        )}
      </Modal>
    </>
  );
};

export default GoalReviews;
